import { useEffect, useState } from "react";
import { AppLayout } from "@/components/layout/AppLayout";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Send, LifeBuoy, Mail, Save } from "lucide-react";
import { toast } from "sonner";

const TYPE_TONES = {
  Bug: "bg-rose-50 text-rose-700 border-rose-200",
  Enhancement: "bg-sky-50 text-sky-700 border-sky-200",
  Question: "bg-slate-50 text-slate-600 border-slate-200",
  "Data Issue": "bg-amber-50 text-amber-700 border-amber-200",
  "Access Request": "bg-violet-50 text-violet-700 border-violet-200",
  Other: "bg-slate-50 text-slate-600 border-slate-200",
};
const STATUS_TONES = {
  open: "bg-amber-50 text-amber-700 border-amber-200",
  in_progress: "bg-sky-50 text-sky-700 border-sky-200",
  resolved: "bg-emerald-50 text-emerald-700 border-emerald-200",
};
const STATUS_LABEL = { open: "Open", in_progress: "In Progress", resolved: "Resolved" };
const fmtDate = (iso) => new Date(iso).toLocaleString("en-AU", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });

export default function SupportPage() {
  const [meta, setMeta] = useState(null);
  const [tickets, setTickets] = useState(null);
  const [form, setForm] = useState({ type: "Bug", module: "", custom_module: "", description: "" });
  const [submitting, setSubmitting] = useState(false);
  const [recipient, setRecipient] = useState("");
  const [detail, setDetail] = useState(null);
  const [reply, setReply] = useState("");
  const [replying, setReplying] = useState(false);

  const load = async () => {
    try {
      const [m, t] = await Promise.all([api.get("/support/meta"), api.get("/support/tickets")]);
      setMeta(m.data); setTickets(t.data);
      if (m.data.recipient) setRecipient(m.data.recipient);
    } catch (e) { toast.error(e.response?.data?.detail || "Support load failed"); }
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.module) { toast.error("Select a module"); return; }
    if (form.module === "__custom__" && !form.custom_module.trim()) { toast.error("Enter the new module name"); return; }
    if (form.description.trim().length < 10) { toast.error("Please describe the request (min 10 characters)"); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/support/tickets", form);
      toast.success(`${data.ticket_id} submitted${data.email_sent ? ` — emailed to ${data.email_to}` : ""}`);
      setForm({ type: "Bug", module: "", custom_module: "", description: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Submit failed"); }
    finally { setSubmitting(false); }
  };

  const setStatus = async (ticket_id, status) => {
    try {
      await api.patch(`/support/tickets/${ticket_id}`, { status });
      toast.success(`${ticket_id} → ${STATUS_LABEL[status]}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };

  const saveRecipient = async () => {
    try {
      const { data } = await api.put("/support/config", { recipient });
      toast.success(`Support requests now route to ${data.recipient}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const sendReply = async () => {
    if (reply.trim().length < 2) { toast.error("Reply cannot be empty"); return; }
    setReplying(true);
    try {
      await api.post(`/support/tickets/${detail.ticket_id}/replies`, { message: reply });
      setReply("");
      const { data } = await api.get("/support/tickets");
      setTickets(data);
      setDetail(data.tickets.find((x) => x.ticket_id === detail.ticket_id) || null);
      toast.success("Reply sent");
    } catch (e) { toast.error(e.response?.data?.detail || "Reply failed"); }
    finally { setReplying(false); }
  };

  const isManager = tickets?.is_manager;

  return (
    <AppLayout title="Support" subtitle="Report a bug, request an enhancement or ask a question">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5" data-testid="support-page">
        {/* --- Form --- */}
        <div className="lg:col-span-1 space-y-4">
          <div className="rounded-lg border border-slate-200 bg-white p-4">
            <div className="flex items-center gap-2 mb-3">
              <LifeBuoy className="w-4 h-4 text-gbe" />
              <span className="text-sm font-semibold text-slate-800">New support request</span>
            </div>
            <label className="block text-xs mb-3">
              <span className="text-slate-600">Request type</span>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger className="h-8 mt-1 text-xs" data-testid="support-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(meta?.types || []).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </label>
            <label className="block text-xs mb-3">
              <span className="text-slate-600">Module</span>
              <Select value={form.module} onValueChange={(v) => setForm({ ...form, module: v })}>
                <SelectTrigger className="h-8 mt-1 text-xs" data-testid="support-module">
                  <SelectValue placeholder="Select the module this relates to" />
                </SelectTrigger>
                <SelectContent>
                  {(meta?.modules || []).map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                  <SelectItem value="__custom__">+ Add new module…</SelectItem>
                </SelectContent>
              </Select>
            </label>
            {form.module === "__custom__" && (
              <label className="block text-xs mb-3">
                <span className="text-slate-600">New module name</span>
                <Input value={form.custom_module} data-testid="support-custom-module"
                       placeholder="e.g. Timesheets"
                       onChange={(e) => setForm({ ...form, custom_module: e.target.value })}
                       className="h-8 mt-1" />
              </label>
            )}
            <label className="block text-xs mb-3">
              <span className="text-slate-600">Description</span>
              <Textarea value={form.description} data-testid="support-description"
                        rows={5} placeholder="What happened / what do you need? Include steps if it's a bug."
                        onChange={(e) => setForm({ ...form, description: e.target.value })}
                        className="mt-1 text-xs" />
            </label>
            <Button size="sm" className="w-full bg-gbe hover:bg-gbe-dark text-white" disabled={submitting}
                    onClick={submit} data-testid="support-submit">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
              Submit request
            </Button>
          </div>

          {/* Admin routing box */}
          {meta?.is_manager && (
            <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid="support-routing-box">
              <div className="flex items-center gap-2 mb-2">
                <Mail className="w-4 h-4 text-slate-500" />
                <span className="text-sm font-semibold text-slate-800">Request routing</span>
              </div>
              <p className="text-[11px] text-slate-500 mb-2">
                New requests are emailed to this address (e.g. change to support@gbelectrical.com.au later).
              </p>
              <div className="flex gap-2">
                <Input value={recipient} data-testid="support-recipient"
                       onChange={(e) => setRecipient(e.target.value)} className="h-8 text-xs" />
                <Button size="sm" variant="outline" className="h-8" onClick={saveRecipient}
                        data-testid="support-recipient-save"><Save className="w-3.5 h-3.5" /></Button>
              </div>
            </div>
          )}
        </div>

        {/* --- Ticket register --- */}
        <div className="lg:col-span-2">
          <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
            <div className="px-4 py-2.5 border-b border-slate-200 flex items-center justify-between">
              <span className="text-sm font-semibold text-slate-800">
                {isManager ? "All support requests" : "My requests"}
              </span>
              <span className="text-[11px] text-slate-500">{tickets?.tickets?.length ?? 0} tickets</span>
            </div>
            {!tickets ? (
              <div className="flex items-center gap-2 text-sm text-slate-500 py-10 justify-center">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
            ) : tickets.tickets.length === 0 ? (
              <div className="text-center text-xs text-slate-400 py-12" data-testid="support-empty">
                No requests yet — submit your first on the left.</div>
            ) : (
              <table className="w-full text-xs">
                <thead className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase text-slate-500">
                  <tr>
                    <th className="text-left px-3 py-2">Ticket</th>
                    <th className="text-left px-3 py-2">Type</th>
                    <th className="text-left px-3 py-2">Module</th>
                    <th className="text-left px-3 py-2">Description</th>
                    {isManager && <th className="text-left px-3 py-2">By</th>}
                    <th className="text-left px-3 py-2">Submitted</th>
                    <th className="text-left px-3 py-2">Status</th>
                  </tr>
                </thead>
                <tbody data-testid="support-ticket-list">
                  {tickets.tickets.map((t) => (
                    <tr key={t.ticket_id} className="border-b border-slate-100 hover:bg-slate-50/50 align-top">
                      <td className="px-3 py-2 font-mono text-[10px] whitespace-nowrap">
                        <button onClick={() => { setDetail(t); setReply(""); }}
                                data-testid={`support-open-${t.ticket_id}`}
                                className="text-gbe hover:underline font-semibold">{t.ticket_id}</button>
                        {(t.replies || []).length > 0 && (
                          <span className="ml-1 text-slate-400">({t.replies.length})</span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <Badge variant="outline" className={`text-[10px] ${TYPE_TONES[t.type] || TYPE_TONES.Other}`}>{t.type}</Badge>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-slate-700">{t.module}</td>
                      <td className="px-3 py-2 text-slate-600 max-w-[280px]">
                        <div className="line-clamp-3" title={t.description}>{t.description}</div>
                      </td>
                      {isManager && <td className="px-3 py-2 whitespace-nowrap text-slate-500">{t.created_by?.name}</td>}
                      <td className="px-3 py-2 whitespace-nowrap text-slate-500">{fmtDate(t.created_at)}</td>
                      <td className="px-3 py-2">
                        {isManager ? (
                          <Select value={t.status} onValueChange={(v) => setStatus(t.ticket_id, v)}>
                            <SelectTrigger className={`h-7 w-32 text-[10px] border ${STATUS_TONES[t.status]}`}
                                           data-testid={`support-status-${t.ticket_id}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {Object.entries(STATUS_LABEL).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        ) : (
                          <Badge variant="outline" className={`text-[10px] ${STATUS_TONES[t.status]}`}>
                            {STATUS_LABEL[t.status]}
                          </Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {/* Ticket thread dialog */}
      {detail && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-start justify-center p-6 overflow-y-auto"
             onClick={() => setDetail(null)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-xl mt-10 max-h-[85vh] flex flex-col overflow-hidden"
               onClick={(e) => e.stopPropagation()} data-testid="support-thread-modal">
            <div className="px-5 py-3 border-b border-slate-200">
              <div className="flex items-center gap-2">
                <span className="font-mono text-xs text-slate-500">{detail.ticket_id}</span>
                <Badge variant="outline" className={`text-[10px] ${TYPE_TONES[detail.type] || TYPE_TONES.Other}`}>{detail.type}</Badge>
                <Badge variant="outline" className={`text-[10px] ${STATUS_TONES[detail.status]}`}>{STATUS_LABEL[detail.status]}</Badge>
                <button onClick={() => setDetail(null)} className="ml-auto text-slate-400 hover:text-slate-700 text-lg leading-none"
                        data-testid="support-thread-close">×</button>
              </div>
              <div className="text-sm font-semibold text-slate-800 mt-1">{detail.module}</div>
              <div className="text-[11px] text-slate-500">{detail.created_by?.name} · {fmtDate(detail.created_at)}</div>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="support-thread">
              <div className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-xs text-slate-700 whitespace-pre-wrap">
                {detail.description}
              </div>
              {(detail.replies || []).map((r, i) => (
                <div key={i} className={`rounded-lg px-3 py-2 text-xs whitespace-pre-wrap border ${
                  r.is_manager ? "bg-sky-50 border-sky-200 text-slate-800" : "bg-white border-slate-200 text-slate-700"}`}>
                  <div className="text-[10px] text-slate-400 mb-1">
                    {r.by?.name}{r.is_manager ? " · GBE Support" : ""} · {fmtDate(r.at)}
                  </div>
                  {r.message}
                </div>
              ))}
            </div>
            <div className="border-t border-slate-200 p-3">
              <div className="flex gap-2">
                <Textarea value={reply} rows={2} placeholder="Write a reply…" className="text-xs flex-1"
                          data-testid="support-reply-input"
                          onChange={(e) => setReply(e.target.value)} />
                <Button size="sm" className="bg-gbe hover:bg-gbe-dark text-white self-end" disabled={replying}
                        onClick={sendReply} data-testid="support-reply-send">
                  {replying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </Button>
              </div>
              <div className="text-[10px] text-slate-400 mt-1">The other party is notified by email.</div>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}
